import { useCallback, useMemo, useState } from 'react';
import { useSanitizedHtml } from 'hooks/useSanitizedHtml';
import { PageSizing } from 'components/Layout/PageContentWidth';
import Cols from 'components/Layout/Cols';
import TextInput from 'components/Inputs/TextInput';
import { useTranslation } from 'react-i18next';
import Switch from 'components/Kizen/Switch';
import { Tooltip, useTooltip } from '@kizen/kds/Tooltip';
import Icon from 'components/Kizen/Icon';
import SwitchControl from 'components/Wizards/CustomObject/components/SwitchControl';
import ConfirmationModal from 'components/Modals/presets/ConfirmationModal';
import { KeyBoardContext } from 'hooks/keyboardEventHandler/useKeyBoardContext';
import { useKeyListeners } from 'hooks/keyboardEventHandler/useKeyListeners';
import useModal from 'components/Modals/useModal';
import BuilderCard from '../../components/BuilderCard';
import Section from '../../components/Section';
import Row from '../../components/Row';
import { getObjectType, PIPELINE_CO } from '../../utilities';
import { getDataQAForInput } from 'components/Inputs/helpers';
import { ConfirmModalWithInput } from '../../components/ConfirmModalWithInput';
import { useCustomObjectWizard } from 'components/Wizards/CustomObject/CustomObjectWizardContext';
import { PreReleaseFeature } from 'hooks/usePreReleaseFeatures';
import {
  StyledColorPickerInput,
  StyledIconSelector,
  StyledTemplateButton,
  StyledTemplateButtonWrapper,
} from './styles';
import { EditNameTemplateModal } from './EditNameTemplateModal';
import { iconOptions } from 'components/MultiColumnBuilder/components/IconSelector';
import { EntitySearchPatternTextSelect } from './EntitySearchPatternTextSelect';
import { Description } from 'pages/Common/components/Description';
import { blue } from '@kizen/kds/Colors';

const TOOLTIP_SANITIZE_OPTIONS = {
  ALLOWED_TAGS: ['a'],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'style'],
};

const ApiDocsLabelInfo = ({ apiDocsUrl }) => {
  const { t } = useTranslation();
  const { triggerProps, targetProps, showTooltip, tooltipProps } =
    useTooltip(true);

  const tooltipHtml = useSanitizedHtml(
    `${t(
      'The unique system name used to reference this object in APIs and integrations. Changing it may break connected workflows.'
    )} <a href="${apiDocsUrl}" target="_blank" rel="noopener noreferrer" style="color:var(--color-button-secondary-default);text-decoration:underline;">${t('View API Docs')}</a>`,
    TOOLTIP_SANITIZE_OPTIONS
  );

  return (
    <span
      style={{
        display: 'inline-block',
        marginLeft: 4,
        verticalAlign: 'middle',
        marginBottom: 2,
      }}
      {...targetProps}
      {...triggerProps}
    >
      {showTooltip && (
        <Tooltip
          interactive
          html={tooltipHtml}
          position="right"
          modalCompatibility
          {...tooltipProps}
        />
      )}
      <Icon icon="info-circle" width="10px" />
    </span>
  );
};

const DefaultIconSelector = ({ onChange, value }) => {
  const { t } = useTranslation();
  const iconOptionsLookup = useMemo(
    () =>
      iconOptions.reduce((acc, el) => {
        acc[el.value] = el;
        return acc;
      }, {}),
    []
  );
  return (
    <div>
      <StyledIconSelector
        label={t('Default Icon')}
        value={iconOptionsLookup[value]}
        onChange={onChange}
      />
    </div>
  );
};
export default function GeneralSettings({
  label,
  updateStepField,
  objectContainsWorkflow,
  formData,
  errors,
  validationFormProps,
  ...props
}) {
  const { validationProps, handleInputChange } = validationFormProps;
  const { isCreateMode, isContact } = useCustomObjectWizard();
  const { t } = useTranslation();
  const [confirmationModalProps, , confirmationModal] = useModal();
  const [modalProps, , modal] = useModal();
  const [confirmType, setConfirmType] = useState(null);
  const [nameTemplateData, setNameTemplateData] = useState({
    content: formData.entityDisplayNamePatternText,
    htmlContent: formData.entityDisplayNamePatternHtml,
  });

  const confirmationManager = useMemo(
    () => ({
      trackEntityValue: {
        message: t(
          'This will disable entity value tracking on your records, but keep your data if you choose to reactivate. Pipeline dashlets with $ values will no longer function.'
        ),
        onConfirm: () => {
          confirmationModal.hide();
          updateStepField('trackEntityValue', false);
        },
      },
      defaultOnActivities: {
        message: t(
          'This will hide the custom object association with submitted activities. You will no longer be able to assign these entity records to scheduled or logged activities.'
        ),
        onConfirm: () => {
          confirmationModal.hide();
          updateStepField('defaultOnActivities', false);
        },
      },
      allowOnForms: {
        message: t(
          "This will disable this object's entities from displaying on public forms/surveys. Forms/Surveys with this field will have no options to choose from."
        ),
        onConfirm: () => {
          confirmationModal.hide();
          updateStepField('allowOnForms', false);
        },
      },
    }),
    [t, updateStepField, confirmationModal]
  );

  // modal handlers
  const handleConfirmationConfirm = useCallback(() => {
    modal.hide();
    updateStepField('allowOnForms', !formData?.allowOnForms);
  }, [modal, updateStepField, formData?.allowOnForms]);

  const { assignFieldHandle, getKeyListenersProps } = useKeyListeners([
    { id: 'object_name' },
    {
      id: 'entity_name',
    },
    { id: 'object_contains_workflow' },
    { id: 'default_on_activities' },
    { id: 'description' },
    { id: 'track_entity_value' },
    { id: 'allow_on_forms' },
    { id: 'has_commerce_data' },
  ]);

  const onToggleChange = useCallback(
    (e, type) => {
      if (isCreateMode) {
        updateStepField(type, e.target.checked);
      } else {
        if (e.target.checked) {
          updateStepField(type, true);
          return;
        }
        e.preventDefault();
        setConfirmType(type);
        confirmationModal.show();
      }
    },
    [confirmationModal, isCreateMode, updateStepField]
  );

  const [templateModalProps, templateButtonProps] = useModal({
    handleSubmit: setNameTemplateData,
  });

  const handleChangeDescription = useCallback(
    (value) => {
      const trimmed = value.trim();
      if (
        (trimmed || formData.description) &&
        trimmed !== formData.description
      ) {
        handleInputChange('description', trimmed);
      }
    },
    [handleInputChange, formData.description]
  );

  const handleChangeDefaultColor = useCallback(
    (value) => {
      updateStepField('defaultColor', value);
    },
    [updateStepField]
  );

  const handleChangeDefaultIcon = useCallback(
    ({ value }) => {
      updateStepField('defaultIcon', value);
    },
    [updateStepField]
  );

  const handleChangeEntitySearchTextPattern = useCallback(
    (value, ev, err) => {
      const next = value.reduce((acc, { value }) => acc + `{{ ${value} }}`, '');
      handleInputChange('entitySearchTextPattern', next, err);
    },
    [handleInputChange]
  );

  return (
    <PageSizing>
      <KeyBoardContext.Provider value={{ assignFieldHandle }}>
        <BuilderCard {...props} label={label}>
          <Section>
            <Row>
              <Cols columns={2} gutter="20px">
                <TextInput
                  label={t('Object Name')}
                  onChange={(value, ev, err) => {
                    handleInputChange('objectName', value, err);
                  }}
                  placeholder={t('Support Pipeline, Apartments, etc.')}
                  inModal
                  disabled={isContact}
                  value={formData.objectName}
                  labelInfo={t('What will show on the toolbar.')}
                  {...getKeyListenersProps('object_name')}
                  {...getDataQAForInput('object_name')}
                  {...validationProps('objectName')}
                  required
                />
                <TextInput
                  label={t('Entity Name')}
                  onChange={(value, ev, err) => {
                    handleInputChange('entityName', value, err);
                  }}
                  placeholder={t('Ticket, Unit, etc.')}
                  inModal
                  disabled={isContact}
                  value={formData.entityName}
                  labelInfo={t(
                    'Also known as Record Name. Often singular version of Object Name.'
                  )}
                  {...getKeyListenersProps('entity_name')}
                  {...getDataQAForInput('entity_name')}
                  {...validationProps('entityName')}
                  required
                />
              </Cols>
            </Row>
            {isCreateMode ? null : (
              <Row>
                <Cols columns={2} gutter="20px">
                  <TextInput
                    label={
                      <>
                        {t('Object API Name')}
                        {formData.name && (
                          <ApiDocsLabelInfo
                            apiDocsUrl={`${import.meta.env.VITE_API_BASE_PATH}/record-docs/${encodeURIComponent(formData.name)}/swagger`}
                          />
                        )}
                      </>
                    }
                    inModal
                    disabled
                    value={formData.name}
                  />
                  <TextInput
                    label={t('Object ID')}
                    inModal
                    disabled
                    value={formData.id}
                  />
                </Cols>
              </Row>
            )}
            <Row>
              <Cols columns={2} gutter="20px">
                <Cols columns={2} gutter="20px">
                  {!isContact ? (
                    <SwitchControl
                      label={t('Contains Workflow')}
                      labelInfo={t(
                        'Toggle this setting on if your entities will be moving through a workflow with open and closed stages. Examples include Sales Pipelines, Support Ticketing, and Project Management.'
                      )}
                    >
                      <Switch
                        disabled={!isCreateMode}
                        checked={formData.objectType === PIPELINE_CO}
                        onChange={(e) => {
                          if (!isCreateMode) return;
                          return updateStepField(
                            'objectType',
                            getObjectType(e.target.checked)
                          );
                        }}
                        {...getKeyListenersProps('object_contains_workflow')}
                        {...getDataQAForInput('object_contains_workflow')}
                      />
                    </SwitchControl>
                  ) : null}
                  <SwitchControl
                    label={t('Enable Quick Filters')}
                    labelInfo={t(
                      'Quick filters enable a second form of less-detailed filtering on records, chart groups, and board views that are pre-defined for ease of use.'
                    )}
                  >
                    <Switch
                      checked={formData.quickFilteringEnabled}
                      onChange={(e) =>
                        updateStepField(
                          'quickFilteringEnabled',
                          e.target.checked
                        )
                      }
                      {...getKeyListenersProps('quick_filtering_enabled')}
                      {...getDataQAForInput('quick_filtering_enabled')}
                    />
                  </SwitchControl>
                </Cols>
                {isCreateMode || isContact ? null : (
                  <Cols columns={2} gutter="20px">
                    <TextInput
                      label={t('Display Name Template')}
                      inModal
                      disabled
                      readOnly
                      value={nameTemplateData.content}
                    />
                    <StyledTemplateButtonWrapper>
                      <StyledTemplateButton
                        variant="text"
                        color="blue"
                        {...templateButtonProps}
                      >
                        {t('Edit Name Template')}
                      </StyledTemplateButton>
                    </StyledTemplateButtonWrapper>
                  </Cols>
                )}
              </Cols>
            </Row>
            <Row>
              <Description
                label={t('Object Description')}
                description={formData.description}
                aiDescription={formData.aiDescription}
                onChange={handleChangeDescription}
                inputProps={{
                  ...getKeyListenersProps('description'),
                  ...getDataQAForInput('description'),
                  ...validationProps('description'),
                }}
              />
            </Row>
            <Row>
              <Cols columns={2} gutter="20px">
                <Cols columns={2} gutter="20px">
                  <StyledColorPickerInput
                    label={t('Default Color')}
                    color={formData.defaultColor ?? blue['blue-800']}
                    onChange={handleChangeDefaultColor}
                  />
                  <DefaultIconSelector
                    value={formData.defaultIcon ?? 'bars-light'}
                    onChange={handleChangeDefaultIcon}
                  />
                </Cols>
                {isCreateMode || isContact ? null : (
                  <EntitySearchPatternTextSelect
                    objectId={formData.id}
                    entitySearchTextPattern={formData.entitySearchTextPattern}
                    onChange={handleChangeEntitySearchTextPattern}
                    validationProps={validationProps('entitySearchTextPattern')}
                  />
                )}
              </Cols>
            </Row>
          </Section>
          <Section label={t('Choose Where to Include the Object')}>
            <Row>
              <Cols columns={4} gutter="20px">
                <SwitchControl
                  label={t('Enable Activities')}
                  labelInfo={t(
                    'Associate this object with logged or scheduled activities.'
                  )}
                >
                  <Switch
                    checked={formData?.defaultOnActivities || false}
                    onChange={(e) => onToggleChange(e, 'defaultOnActivities')}
                    {...getKeyListenersProps('default_on_activities')}
                    {...getDataQAForInput('default_on_activities')}
                    disabled={isContact}
                  />
                </SwitchControl>
                {!isContact ? (
                  <>
                    <SwitchControl label={t('Track Entity $ Value')}>
                      <Switch
                        checked={formData?.trackEntityValue || false}
                        onChange={(e) => onToggleChange(e, 'trackEntityValue')}
                        {...getKeyListenersProps('track_entity_value')}
                        {...getDataQAForInput('track_entity_value')}
                      />
                    </SwitchControl>
                    <PreReleaseFeature>
                      <SwitchControl
                        label={t('Allow as Field on Public Forms/Surveys')}
                        labelInfo={t(
                          'Toggle this setting on to allow entity record fields to display on public forms. This should not be used with sensitive data that should not be available to the public.'
                        )}
                      >
                        <Switch
                          checked={formData?.allowOnForms || false}
                          onChange={(e) => {
                            e.preventDefault();
                            if (!e.target.checked) {
                              setConfirmType('allowOnForms');
                              confirmationModal.show();
                            } else {
                              modal.show();
                            }
                          }}
                          {...getKeyListenersProps('allow_on_forms')}
                          {...getDataQAForInput('allow_on_forms')}
                        />
                      </SwitchControl>
                    </PreReleaseFeature>
                  </>
                ) : null}
              </Cols>
            </Row>
          </Section>
        </BuilderCard>
        <ConfirmModalWithInput
          className={`no-drag`}
          heading={t('Please Confirm Setting')}
          buttonText={t('Confirm')}
          defaultLeftBtnText={t('Cancel')}
          actionBtnColor="green"
          {...modalProps}
          onConfirm={handleConfirmationConfirm}
          message={t(
            'This setting will allow entity records to display on public forms. Please confirm this object has no sensitive data (PII, etc.) that should not be available to the public. Please type “{{name}}” below to confirm.',
            {
              name: formData.objectName || 'ALLOW',
            }
          )}
          unblockName={formData.objectName || 'ALLOW'}
        />
        <ConfirmationModal
          className={`no-drag`}
          heading={t('Please Confirm Setting')}
          buttonText={t('Confirm')}
          defaultLeftBtnText={t('Cancel')}
          actionBtnColor="green"
          {...confirmationModalProps}
          {...confirmationManager[confirmType]}
        >
          {confirmationManager[confirmType]?.message}
        </ConfirmationModal>
        {templateModalProps.show ? (
          <EditNameTemplateModal
            {...templateModalProps}
            data={nameTemplateData}
            customObject={formData}
          />
        ) : null}
      </KeyBoardContext.Provider>
    </PageSizing>
  );
}
